"use client";

import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isEpochMs, isIsoDate } from "../detect";
import { PRIORITY, type ValueRenderer } from "../types";

function relativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const absSec = Math.abs(diffMs) / 1000;
  const future = diffMs < 0;
  const units: [number, string][] = [
    [60, "s"],
    [3600, "m"],
    [86400, "h"],
    [2592000, "d"],
    [31536000, "mo"],
  ];
  let label = `${Math.round(absSec / 31536000)}y`;
  for (let i = 0; i < units.length; i++) {
    if (absSec < units[i][0]) {
      const divisor = i === 0 ? 1 : units[i - 1][0];
      label = `${Math.round(absSec / divisor)}${units[i][1]}`;
      break;
    }
  }
  return future ? `in ${label}` : `${label} ago`;
}

export const DateRenderer: ValueRenderer = {
  id: "date",
  match: value => {
    if (isIsoDate(value)) return { priority: PRIORITY.CONTENT_SNIFFED, confidence: "high" };
    if (isEpochMs(value)) return { priority: PRIORITY.CONTENT_SNIFFED, confidence: "low" };
    return null;
  },
  Component: ({ value }) => {
    // `toLocaleString()` (locale/timezone) and `relativeTime()` (Date.now())
    // are environment-dependent, so they'd mismatch between SSR and the client.
    // Render the stable raw value until mounted, then the localized version.
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    const date = typeof value === "number" ? new Date(value) : new Date(value as string);

    if (!mounted) {
      return <span className="text-ds-text-primary tabular-nums">{String(value)}</span>;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-ds-text-primary tabular-nums">
              {date.toLocaleString()} <span className="text-ds-text-muted">({relativeTime(date)})</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>{String(value)}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
};
